import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  FileText, CheckCircle2, Clock, DollarSign, Building2,
  Search, ChevronDown, ChevronUp, AlertCircle, Trash2, X, MessageSquare
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { MentionTextarea } from '../components/MentionTextarea';
import { useUserNames } from '../context/UserNamesContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InvoiceVerification({ embedded = false }) {
  const [groupedInvoices, setGroupedInvoices] = useState([]);
  const [verifiedInvoices, setVerifiedInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedAgencies, setExpandedAgencies] = useState({});
  const [verifying, setVerifying] = useState({});
  const [showVerified, setShowVerified] = useState(false);
  
  // Verification modal state
  const [verifyModal, setVerifyModal] = useState({ open: false, invoice: null });
  const [verifyNotes, setVerifyNotes] = useState('');
  const [submittingVerify, setSubmittingVerify] = useState(false);
  
  // Note modal state (for adding notes without verifying)
  const [noteModal, setNoteModal] = useState({ open: false, invoice: null });
  const [noteText, setNoteText] = useState('');
  const [noteMentions, setNoteMentions] = useState([]);
  const { resolveName } = useUserNames();
  const [submittingNote, setSubmittingNote] = useState(false);
  
  // Assign agency modal state
  const [assignModal, setAssignModal] = useState({ open: false, invoice: null });
  const [agencies, setAgencies] = useState([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState('');
  const [submittingAssign, setSubmittingAssign] = useState(false);
  const [rematchingAll, setRematchingAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSelect = (invoiceId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(invoiceId)) next.delete(invoiceId); else next.add(invoiceId);
      return next;
    });
  };

  const toggleSelectGroup = (invoices, allSelected) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      invoices.forEach((inv) => { if (allSelected) next.delete(inv.id); else next.add(inv.id); });
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkVerify = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Verify ${ids.length} selected invoice${ids.length !== 1 ? 's' : ''}?`)) return;
    setBulkBusy(true);
    try {
      await axios.post(`${API}/invoices/bulk-verify`, { invoice_ids: ids });
      toast.success(`Verified ${ids.length} invoice${ids.length !== 1 ? 's' : ''}`);
      clearSelection();
      fetchInvoices();
      fetchStats();
    } catch (error) {
      console.error('Bulk verify failed:', error);
      toast.error('Failed to verify selected invoices');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected invoice${ids.length !== 1 ? 's' : ''}? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      await axios.post(`${API}/invoices/bulk-delete`, { invoice_ids: ids });
      toast.success(`Deleted ${ids.length} invoice${ids.length !== 1 ? 's' : ''}`);
      clearSelection();
      fetchInvoices();
      fetchStats();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error('Failed to delete selected invoices');
    } finally {
      setBulkBusy(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchStats();
    fetchAgencies();
  }, []);

  const fetchAgencies = async () => {
    try {
      const response = await axios.get(`${API}/agency-profiles`);
      setAgencies(response.data || []);
    } catch (error) {
      console.error('Error fetching agencies:', error);
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      // Fetch ALL invoices (both verified and unverified) to ensure consistency with AgencyDetail page
      const response = await axios.get(`${API}/invoices`);
      const allInvoices = response.data || [];
      
      // Separate verified and unverified
      const unverified = allInvoices.filter(inv => !inv.verified);
      const verified = allInvoices.filter(inv => inv.verified);
      
      // Group unverified by agency (same logic as backend, but client-side for consistency)
      const groups = {};
      
      const normalizeAgencyName = (name) => {
        if (!name) return "Unknown Agency";
        let n = name.toLowerCase().trim();
        // Remove common suffixes for grouping key
        for (const suffix of [', inc.', ', inc', ' inc.', ' inc', ', llc', ' llc', ', ltd', ' ltd']) {
          n = n.replace(suffix, '');
        }
        return n.trim();
      };
      
      for (const inv of unverified) {
        // Group ALL invoices by normalized agency name so duplicate/mismatched
        // agency records (e.g. "New Day" appearing several times) collapse into
        // ONE group. We keep a representative agency_id (first non-null seen).
        const agencyId = inv.agency_id;
        const agencyName = inv.agency_name || "Unknown Agency";
        const normalized = normalizeAgencyName(agencyName);
        const groupKey = `name:${normalized}`;
        const displayName = agencyName;

        if (!groups[groupKey]) {
          groups[groupKey] = {
            agency_name: displayName,
            agency_id: agencyId || null,
            invoices: [],
            total_amount: 0,
            count: 0
          };
        }

        // Adopt the first real agency_id we encounter for the group.
        if (!groups[groupKey].agency_id && agencyId) {
          groups[groupKey].agency_id = agencyId;
        }
        // Update display name if we find a longer/more complete version
        if (agencyName.length > groups[groupKey].agency_name.length) {
          groups[groupKey].agency_name = agencyName;
        }

        groups[groupKey].invoices.push({
          id: inv.id,
          invoice_number: inv.invoice_number,
          invoice_date: inv.invoice_date,
          amount: inv.amount || 0,
          uploaded_at: inv.uploaded_at,
          verification_notes: inv.verification_notes,
          notes_updated_by: inv.notes_updated_by,
          notes_updated_at: inv.notes_updated_at,
          agency_id: agencyId
        });
        groups[groupKey].total_amount += inv.amount || 0;
        groups[groupKey].count += 1;
      }
      
      // Convert to list and sort ALPHABETICALLY by agency name
      const groupedResult = Object.values(groups).sort((a, b) =>
        a.agency_name.toLowerCase().localeCompare(b.agency_name.toLowerCase())
      );
      
      setGroupedInvoices(groupedResult);
      setVerifiedInvoices(verified);
      
      // Expand first agency by default
      if (groupedResult.length > 0) {
        setExpandedAgencies({ [groupedResult[0].agency_name]: true });
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/invoices/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleVerify = async (invoiceId) => {
    setVerifying({ ...verifying, [invoiceId]: true });
    try {
      await axios.post(`${API}/invoices/verify/${invoiceId}`);
      toast.success('Invoice verified');
      fetchInvoices();
      fetchStats();
    } catch (error) {
      console.error('Error verifying invoice:', error);
      toast.error('Failed to verify invoice');
    } finally {
      setVerifying({ ...verifying, [invoiceId]: false });
    }
  };

  const openVerifyModal = (invoice) => {
    setVerifyModal({ open: true, invoice });
    setVerifyNotes('');
  };

  const closeVerifyModal = () => {
    setVerifyModal({ open: false, invoice: null });
    setVerifyNotes('');
  };

  const submitVerification = async () => {
    if (!verifyModal.invoice) return;
    
    setSubmittingVerify(true);
    try {
      await axios.post(`${API}/invoices/verify/${verifyModal.invoice.id}`, {
        notes: verifyNotes.trim() || null
      });
      toast.success('Invoice verified');
      closeVerifyModal();
      fetchInvoices();
      fetchStats();
    } catch (error) {
      console.error('Error verifying invoice:', error);
      toast.error('Failed to verify invoice');
    } finally {
      setSubmittingVerify(false);
    }
  };

  // Note modal functions
  const openNoteModal = (invoice) => {
    setNoteModal({ open: true, invoice });
    setNoteText(invoice.verification_notes || '');
    setNoteMentions([]);
  };

  const closeNoteModal = () => {
    setNoteModal({ open: false, invoice: null });
    setNoteText('');
    setNoteMentions([]);
  };

  const submitNote = async () => {
    if (!noteModal.invoice) return;
    
    setSubmittingNote(true);
    try {
      await axios.post(`${API}/invoices/${noteModal.invoice.id}/notes`, {
        notes: noteText.trim(),
        mentions: noteMentions,
      });
      toast.success('Note saved');
      closeNoteModal();
      fetchInvoices();
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error('Failed to save note');
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      fetchInvoices();
      fetchStats();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Failed to delete invoice');
    }
  };

  const handleUnverify = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to unverify this invoice?')) return;
    
    try {
      await axios.post(`${API}/invoices/unverify/${invoiceId}`);
      toast.success('Invoice unverified');
      fetchInvoices();
      fetchStats();
    } catch (error) {
      console.error('Error unverifying invoice:', error);
      toast.error('Failed to unverify invoice');
    }
  };

  const openAssignModal = (invoice) => {
    setAssignModal({ open: true, invoice });
    setSelectedAgencyId(invoice.agency_id || '');
  };

  const closeAssignModal = () => {
    setAssignModal({ open: false, invoice: null });
    setSelectedAgencyId('');
  };

  const submitAssignAgency = async () => {
    if (!assignModal.invoice || !selectedAgencyId) return;
    
    setSubmittingAssign(true);
    try {
      await axios.put(`${API}/invoices/${assignModal.invoice.id}?agency_id=${selectedAgencyId}`);
      toast.success('Agency assigned successfully');
      closeAssignModal();
      fetchInvoices();
    } catch (error) {
      console.error('Error assigning agency:', error);
      toast.error('Failed to assign agency');
    } finally {
      setSubmittingAssign(false);
    }
  };

  const handleRematchAll = async (force = false) => {
    const message = force 
      ? 'This will re-match ALL invoices to agencies (even already linked ones). Continue?'
      : 'This will try to match unlinked invoices to agencies based on their names. Continue?';
    
    if (!window.confirm(message)) return;
    
    setRematchingAll(true);
    try {
      const response = await axios.post(`${API}/invoices/rematch-agencies?force=${force}`);
      const { matched, updated, still_unlinked } = response.data;
      toast.success(`Matched ${matched} invoices, updated ${updated}. ${still_unlinked} still unlinked.`);
      fetchInvoices();
      fetchStats();
    } catch (error) {
      console.error('Error rematching:', error);
      toast.error('Failed to rematch invoices');
    } finally {
      setRematchingAll(false);
    }
  };

  const toggleAgency = (agencyName) => {
    setExpandedAgencies({
      ...expandedAgencies,
      [agencyName]: !expandedAgencies[agencyName]
    });
  };

  const agencyPhoneById = {};
  (agencies || []).forEach((a) => { if (a.id) agencyPhoneById[a.id] = (a.phone || ''); });

  const q = searchQuery.trim().toLowerCase();
  const digits = (s) => (s || '').replace(/\D/g, '');
  const qDigits = digits(q);

  const filteredGroups = !q
    ? groupedInvoices
    : groupedInvoices
        .map((group) => {
          const nameMatch = group.agency_name.toLowerCase().includes(q);
          const phone = agencyPhoneById[group.agency_id] || '';
          const phoneMatch = qDigits.length >= 3 && digits(phone).includes(qDigits);
          if (nameMatch || phoneMatch) return group;
          // Otherwise, try to match by invoice number within the group
          const matchingInvoices = group.invoices.filter((inv) =>
            (inv.invoice_number || '').toLowerCase().includes(q)
          );
          if (matchingInvoices.length > 0) {
            return { ...group, invoices: matchingInvoices, count: matchingInvoices.length };
          }
          return null;
        })
        .filter(Boolean);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className={embedded ? "space-y-4" : "p-6 space-y-6"}>
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Invoice Verification</h1>
            <p className="text-muted-foreground mt-1">
              Review and verify agency invoices
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => handleRematchAll(false)}
              disabled={rematchingAll}
              title="Link unlinked invoices to agencies"
            >
              {rematchingAll ? 'Matching...' : 'Rematch Unlinked'}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => handleRematchAll(true)}
              disabled={rematchingAll}
              title="Re-link ALL invoices (fixes mismatches)"
              className="text-amber-600 hover:text-amber-700"
            >
              Force Rematch All
            </Button>
            <Link to="/upload-invoices">
              <Button data-testid="upload-invoice-btn">
                <FileText size={16} className="mr-2" />
                Upload Invoice
              </Button>
            </Link>
            <Link to="/admin/data-cleanup">
              <Button variant="outline" data-testid="data-cleanup-btn" title="Merge duplicate agencies & remove duplicate invoices">
                Data Cleanup
              </Button>
            </Link>
          </div>
        </div>
      )}

      {embedded && (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground">Review and verify agency invoices</p>
          <Link to="/upload-invoices">
            <Button size="sm" data-testid="upload-invoice-btn">
              <FileText size={14} className="mr-1" />
              Upload Invoice
            </Button>
          </Link>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Invoices</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <FileText className="text-blue-500" size={24} />
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Unverified</p>
                <p className="text-2xl font-bold text-orange-600">{stats.unverified}</p>
              </div>
              <Clock className="text-orange-500" size={24} />
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Verified</p>
                <p className="text-2xl font-bold text-green-600">{stats.verified}</p>
              </div>
              <CheckCircle2 className="text-green-500" size={24} />
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Unverified Amount</p>
                <p className="text-2xl font-bold text-orange-600">
                  {formatCurrency(stats.unverified_amount)}
                </p>
              </div>
              <DollarSign className="text-orange-500" size={24} />
            </div>
          </Card>
        </div>
      )}

      {/* Search */}
      <Card className="p-4">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              placeholder="Search by agency, invoice #, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </div>
      </Card>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <Card className="p-3 flex items-center justify-between bg-primary/5 border-primary/30" data-testid="bulk-action-bar">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleBulkVerify} disabled={bulkBusy} data-testid="bulk-verify-btn">
              <CheckCircle2 size={14} className="mr-1" /> Verify Selected
            </Button>
            <Button size="sm" variant="outline" onClick={handleBulkDelete} disabled={bulkBusy} className="text-red-600 hover:text-red-700" data-testid="bulk-delete-btn">
              <Trash2 size={14} className="mr-1" /> Delete Selected
            </Button>
            <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="bulk-clear-btn">
              <X size={14} className="mr-1" /> Clear
            </Button>
          </div>
        </Card>
      )}

      {/* Invoices by Agency */}
      {loading ? (
        <Card className="p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
          </div>
        </Card>
      ) : filteredGroups.length === 0 ? (
        <Card className="p-8 text-center">
          <CheckCircle2 className="mx-auto text-green-500 mb-4" size={48} />
          <h3 className="text-lg font-semibold">All Caught Up!</h3>
          <p className="text-muted-foreground">
            {searchQuery ? 'No matching unverified invoices found.' : 'No unverified invoices at this time.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredGroups.map((group) => (
            <Card key={group.agency_name} className="overflow-hidden">
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => toggleAgency(group.agency_name)}
                data-testid={`agency-row-${group.agency_name}`}
              >
                <div className="flex items-center gap-3">
                  <Building2 className="text-muted-foreground" size={20} />
                  <div>
                    <p className="font-medium">{group.agency_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {group.count} unverified invoice{group.count !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Badge variant="outline" className="text-orange-600 border-orange-300">
                    {formatCurrency(group.total_amount)}
                  </Badge>
                  {expandedAgencies[group.agency_name] ? (
                    <ChevronUp size={20} />
                  ) : (
                    <ChevronDown size={20} />
                  )}
                </div>
              </div>
              
              {expandedAgencies[group.agency_name] && (
                <div className="border-t">
                  <table className="min-w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-2 w-10">
                          <input
                            type="checkbox"
                            checked={group.invoices.length > 0 && group.invoices.every((inv) => selectedIds.has(inv.id))}
                            onChange={() => toggleSelectGroup(group.invoices, group.invoices.every((inv) => selectedIds.has(inv.id)))}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`select-all-${group.agency_name}`}
                            aria-label="Select all in group"
                          />
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-medium">Invoice #</th>
                        <th className="px-4 py-2 text-left text-sm font-medium">Date</th>
                        <th className="px-4 py-2 text-right text-sm font-medium">Amount</th>
                        <th className="px-4 py-2 text-left text-sm font-medium">Notes</th>
                        <th className="px-4 py-2 text-left text-sm font-medium">Uploaded</th>
                        <th className="px-4 py-2 text-right text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.invoices.map((invoice) => (
                        <tr key={invoice.id} className={`border-t hover:bg-muted/30 ${selectedIds.has(invoice.id) ? 'bg-primary/5' : ''}`}>
                          <td className="px-4 py-3 w-10">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(invoice.id)}
                              onChange={() => toggleSelect(invoice.id)}
                              data-testid={`select-invoice-${invoice.id}`}
                              aria-label="Select invoice"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono">{invoice.invoice_number}</span>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {formatDate(invoice.invoice_date)}
                          </td>
                          <td className="px-4 py-3 text-right font-medium">
                            {formatCurrency(invoice.amount)}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground text-sm max-w-[200px] truncate" title={invoice.verification_notes}>
                            {invoice.verification_notes || '-'}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground text-sm">
                            {formatDate(invoice.uploaded_at)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {!invoice.agency_id && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openAssignModal(invoice)}
                                  className="text-amber-600 hover:text-amber-700"
                                  title="Assign to agency"
                                >
                                  <Building2 size={14} />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openNoteModal(invoice)}
                                data-testid={`note-btn-${invoice.id}`}
                              >
                                <MessageSquare size={14} className="mr-1" />
                                Note
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => openVerifyModal(invoice)}
                                data-testid={`verify-btn-${invoice.id}`}
                              >
                                <CheckCircle2 size={14} className="mr-1" />
                                Verify
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDelete(invoice.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Info Card */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-blue-600 mt-0.5" size={20} />
          <div>
            <p className="font-medium text-blue-900">Weekly Verification Reports</p>
            <p className="text-sm text-blue-800 mt-1">
              A report of all unverified invoices is automatically sent every week. 
              Make sure to verify invoices promptly to keep accurate records.
            </p>
          </div>
        </div>
      </Card>

      {/* Verified Invoices Section (Collapsible) */}
      <Card className="overflow-hidden">
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => setShowVerified(!showVerified)}
        >
          <div className="flex items-center gap-3">
            <CheckCircle2 className="text-green-600" size={20} />
            <div>
              <p className="font-medium">Verified Invoices</p>
              <p className="text-sm text-muted-foreground">
                {verifiedInvoices.length} invoice{verifiedInvoices.length !== 1 ? 's' : ''} verified
              </p>
            </div>
          </div>
          {showVerified ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        
        {showVerified && (
          <div className="border-t">
            {verifiedInvoices.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                No verified invoices yet.
              </div>
            ) : (
              <div className="overflow-auto max-h-[400px]">
                <table className="w-full">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium">Invoice #</th>
                      <th className="px-4 py-2 text-left text-sm font-medium">Agency</th>
                      <th className="px-4 py-2 text-left text-sm font-medium">Date</th>
                      <th className="px-4 py-2 text-right text-sm font-medium">Amount</th>
                      <th className="px-4 py-2 text-left text-sm font-medium">Verified By</th>
                      <th className="px-4 py-2 text-left text-sm font-medium">Notes</th>
                      <th className="px-4 py-2 text-right text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {verifiedInvoices.map((invoice) => (
                      <tr key={invoice.id} className="border-t hover:bg-muted/30">
                        <td className="px-4 py-3 font-mono text-sm">{invoice.invoice_number}</td>
                        <td className="px-4 py-3 text-sm">{invoice.agency_name}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {formatDate(invoice.invoice_date)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium">
                          {formatCurrency(invoice.amount)}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {resolveName(invoice.verified_by) || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground max-w-[150px] truncate" title={invoice.verification_notes}>
                          {invoice.verification_notes || '-'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openNoteModal(invoice)}
                            >
                              <MessageSquare size={14} className="mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-orange-600 hover:text-orange-700"
                              onClick={() => handleUnverify(invoice.id)}
                            >
                              <X size={14} className="mr-1" />
                              Unverify
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Verification Modal */}
      {verifyModal.open && verifyModal.invoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-semibold text-lg">Verify Invoice</h2>
              <Button variant="ghost" size="sm" onClick={closeVerifyModal}>
                <X size={18} />
              </Button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Invoice Details */}
              <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Invoice #</span>
                  <span className="font-mono font-medium">{verifyModal.invoice.invoice_number}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-medium">{formatCurrency(verifyModal.invoice.amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Date</span>
                  <span>{formatDate(verifyModal.invoice.invoice_date)}</span>
                </div>
              </div>

              {/* Notes Input */}
              <div>
                <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                  <MessageSquare size={14} />
                  Verification Notes (optional)
                </label>
                <textarea
                  value={verifyNotes}
                  onChange={(e) => setVerifyNotes(e.target.value)}
                  placeholder="Add any notes about this invoice verification..."
                  className="w-full min-h-[100px] p-3 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                  data-testid="verify-notes-input"
                />
              </div>
            </div>

            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={closeVerifyModal}>
                Cancel
              </Button>
              <Button 
                onClick={submitVerification} 
                disabled={submittingVerify}
                data-testid="submit-verify-btn"
              >
                {submittingVerify ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                ) : (
                  <CheckCircle2 size={16} className="mr-2" />
                )}
                Verify Invoice
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Note Modal (for adding notes without verifying) */}
      {noteModal.open && noteModal.invoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-semibold text-lg">Add Note to Invoice</h2>
              <Button variant="ghost" size="sm" onClick={closeNoteModal}>
                <X size={18} />
              </Button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Invoice Details */}
              <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Invoice #</span>
                  <span className="font-mono font-medium">{noteModal.invoice.invoice_number}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-medium">{formatCurrency(noteModal.invoice.amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Date</span>
                  <span>{formatDate(noteModal.invoice.invoice_date)}</span>
                </div>
              </div>

              {/* Notes Input */}
              <div>
                <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                  <MessageSquare size={14} />
                  Note
                </label>
                <MentionTextarea
                  value={noteText}
                  onChange={setNoteText}
                  mentions={noteMentions}
                  onMentionsChange={setNoteMentions}
                  placeholder="Add a note about this invoice… type @ to tag a teammate"
                  rows={4}
                  testId="invoice-note"
                />
              </div>
            </div>

            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={closeNoteModal}>
                Cancel
              </Button>
              <Button 
                onClick={submitNote} 
                disabled={submittingNote || !noteText.trim()}
                data-testid="submit-note-btn"
              >
                {submittingNote ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                ) : (
                  <MessageSquare size={16} className="mr-2" />
                )}
                Save Note
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Assign Agency Modal */}
      {assignModal.open && assignModal.invoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-semibold text-lg">Assign Invoice to Agency</h2>
              <Button variant="ghost" size="sm" onClick={closeAssignModal}>
                <X size={18} />
              </Button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Invoice Details */}
              <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Invoice #</span>
                  <span className="font-mono font-medium">{assignModal.invoice.invoice_number}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Current Agency</span>
                  <span className="font-medium">{assignModal.invoice.agency_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-medium text-green-600">{formatCurrency(assignModal.invoice.amount)}</span>
                </div>
              </div>

              {/* Agency Select */}
              <div>
                <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                  <Building2 size={14} />
                  Select Agency
                </label>
                <select
                  value={selectedAgencyId}
                  onChange={(e) => setSelectedAgencyId(e.target.value)}
                  className="w-full p-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <option value="">-- Select an Agency --</option>
                  {agencies.sort((a, b) => (a.agency_name || '').localeCompare(b.agency_name || '')).map((agency) => (
                    <option key={agency.id} value={agency.id}>
                      {agency.agency_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={closeAssignModal}>
                Cancel
              </Button>
              <Button 
                onClick={submitAssignAgency} 
                disabled={submittingAssign || !selectedAgencyId}
              >
                {submittingAssign ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                ) : (
                  <Building2 size={16} className="mr-2" />
                )}
                Assign Agency
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
